/**
 * "Copy Metadata from [Source]" button. Fetches content references from a parent
 * (series, course) and applies them to the current content via onApply.
 * @module content-detail/content-references/CopyMetadataButton
 */

import React, { useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { DocumentNode } from 'graphql'
import { Button, Tooltip, IconButton, Box } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useDispatch } from 'react-redux'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'
import { showErrorToast } from 'services/toast/redux/actions'
import { transformGraphQLToComponent } from './utils'
import type { ContentReferenceMap, GraphQLContentReference } from '../types'

export interface CopyMetadataSourceConfig {
  triggerRefType: string
  sourceLabel: string
  query: DocumentNode
  buildVariables: (contentId: number) => Record<string, unknown>
  extractReferences: (data: unknown) => GraphQLContentReference[]
  refTypesToCopy: string[]
}

export interface CopyMetadataButtonProps {
  sourceConfig: CopyMetadataSourceConfig
  selectedContentId: number
  onApply: (updates: ContentReferenceMap) => void
  onApplied?: () => void
  disabled?: boolean
}

function CopyMetadataButton ({
  sourceConfig,
  selectedContentId,
  onApply,
  onApplied,
  disabled = false,
}: CopyMetadataButtonProps) {
  const dispatch = useDispatch()
  const dictionary = getContentReferenceTypesContentDictionary()
  const tooltipLabels: string[] = sourceConfig.refTypesToCopy
    .map((refType) => {
      const config = dictionary.get(refType)
      const label = config?.get?.('label')
      return typeof label === 'string' ? label : refType
    })
    .filter(Boolean)

  const [fetchReferences, { loading }] = useLazyQuery(sourceConfig.query, {
    onCompleted: (data) => {
      const rawRefs = sourceConfig.extractReferences(data)
      if (!rawRefs || rawRefs.length === 0) {
        dispatch(
          showErrorToast(
            `No metadata was found in the ${sourceConfig.sourceLabel.toLowerCase()} data to populate fields.`,
          ),
        )
        return
      }
      const transformed = transformGraphQLToComponent(rawRefs)
      const updates: ContentReferenceMap = {}
      sourceConfig.refTypesToCopy.forEach((refType) => {
        const matching = transformed.filter((r) => r.contentReferenceType === refType)
        if (matching.length > 0) {
          updates[refType] = matching
        }
      })
      onApply(updates)
      onApplied?.()
    },
    onError: () => {
      dispatch(
        showErrorToast(
          `An error occurred while trying to grab metadata from ${sourceConfig.sourceLabel.toLowerCase()} data. Please try again or contact support if the issue persists.`,
        ),
      )
    },
  })

  const handleClick = useCallback(() => {
    const variables = sourceConfig.buildVariables(selectedContentId)
    fetchReferences({ variables })
  }, [sourceConfig, selectedContentId, fetchReferences])

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button variant="outlined" size="small" onClick={handleClick} disabled={disabled || loading}>
        {loading ? 'Loading…' : `Copy Metadata from ${sourceConfig.sourceLabel}`}
      </Button>
      <Tooltip
        title={
          <>
            This will copy the following Content References from the{' '}
            {sourceConfig.sourceLabel.toLowerCase()} and apply them to this content:
            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
              {tooltipLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </>
        }
      >
        <IconButton size="small" aria-label="Info">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default CopyMetadataButton
